"use client";

import { useEffect, useRef, useSyncExternalStore, type FormEvent } from "react";

import {
  TimingCitySelector,
  type TimingCityFieldHandle,
} from "@/components/choghadiya/ChoghadiyaCitySelector";
import type { PlaceResult } from "@/lib/api/misc";
import {
  cityParamForPlace,
  normalizeCityParam,
  TIMING_CITY_PARAM,
  TIMING_DATE_PARAM,
  timingHubHref,
  type TimingLocale,
  type TimingTool,
} from "@/lib/timing-city-param";

/**
 * The city (and day) selector for a timing hub: a real `<form translate="no" method="get">`
 * that navigates to `?city=<slug>&date=<YYYY-MM-DD>`.
 *
 * Without JavaScript it is a plain GET form over the 198 catalog cities, so a
 * crawler or a no-JS browser reaches the same computed page a click does (the
 * server HTML always carries that <select>).
 * Once JavaScript runs, the city box becomes one search field prefilled with
 * the current city: typing any Indian (or GeoNames) town opens its timings
 * (xb-02, 2026-10-07: Sitapur was not in the list, and the search sat in a
 * 150x32px caption under the form). Both controls are 44px tall, so the swap
 * moves nothing. A town typed but not picked is resolved on Enter or Show (the
 * first match opens) rather than dropped for the old city. The last choice is
 * remembered in localStorage: a visitor who
 * returns to the bare hub is sent on to the city they last chose. Nothing about
 * the choice enters the server render except through the URL.
 */

export const TIMING_CITY_STORAGE_KEY = "ish_timing_city";

interface CatalogCity {
  slug: string;
  name: string;
  state: string;
}

const COPY = {
  en: {
    city: "City",
    date: "Date",
    show: "Show",
    hint: "Any town or village: type its name in the City box.",
  },
  hi: {
    city: "शहर",
    date: "तारीख",
    show: "देखें",
    hint: "कोई भी शहर या गाँव: शहर वाले बॉक्स में उसका नाम लिखें।",
  },
} as const;

const CONTROL_CLASS =
  "input-sacred mt-1 min-h-11 rounded-lg px-3 py-2 text-body font-normal text-ish-dark";

const NO_SUBSCRIPTION = () => () => {};
/** False in the server HTML and during hydration, true once the client runs. */
function useHydrated(): boolean {
  return useSyncExternalStore(
    NO_SUBSCRIPTION,
    () => true,
    () => false
  );
}

function remember(city: string | null): void {
  if (!city) return;
  try {
    localStorage.setItem(TIMING_CITY_STORAGE_KEY, city);
  } catch {
    /* storage blocked: the URL still carries the choice */
  }
}

function recalled(): string | null {
  try {
    return normalizeCityParam(localStorage.getItem(TIMING_CITY_STORAGE_KEY) ?? undefined);
  } catch {
    return null;
  }
}

export default function TimingCityForm({
  tool,
  locale = "en",
  cities,
  city,
  cityName,
  date,
  today,
  min,
  max,
  dateSelectable = true,
  isBareHub = false,
}: {
  tool: TimingTool;
  locale?: TimingLocale;
  cities: readonly CatalogCity[];
  /** The rendered city's slug (the hub default when no `?city=` resolved). */
  city: string;
  /** Display name, used when `city` is not one of the catalog options. */
  cityName: string;
  date: string;
  today: string;
  min: string;
  max: string;
  /** Shubh Muhurat is a "now" tool and takes no date. */
  dateSelectable?: boolean;
  /** The plain hub URL (no query): restores the remembered city. */
  isBareHub?: boolean;
}) {
  const copy = COPY[locale];
  const action = timingHubHref(tool, locale);
  const inCatalog = cities.some((option) => option.slug === city);
  const hydrated = useHydrated();
  const formRef = useRef<HTMLFormElement>(null);
  const fieldRef = useRef<TimingCityFieldHandle>(null);

  useEffect(() => {
    if (!isBareHub || window.location.search) return;
    const stored = recalled();
    if (stored && stored !== city) {
      window.location.replace(timingHubHref(tool, locale, { city: stored }));
    }
  }, [city, isBareHub, locale, tool]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    // A typed, unpicked town: the field opens its first match. Submitting would
    // send (and remember) the hidden input's old city instead.
    if (fieldRef.current?.commitTyped()) {
      event.preventDefault();
      return;
    }
    const form = event.currentTarget;
    const dateInput = form.elements.namedItem(TIMING_DATE_PARAM);
    // Today's page is the undated URL: omit `date` rather than mint a second
    // address for the same document (and the same edge-cache entry).
    if (dateInput instanceof HTMLInputElement && (!dateInput.value || dateInput.value === today)) {
      dateInput.disabled = true;
    }
    const cityInput = form.elements.namedItem(TIMING_CITY_PARAM);
    remember(
      cityInput instanceof HTMLSelectElement || cityInput instanceof HTMLInputElement
        ? cityInput.value
        : null
    );
  }

  /** The day in the date box now (it may differ from the rendered `date`). */
  function chosenDate(): string {
    const input = formRef.current?.elements.namedItem(TIMING_DATE_PARAM);
    return input instanceof HTMLInputElement && input.value ? input.value : date;
  }

  function handlePlace(place: PlaceResult) {
    const param = cityParamForPlace(place, cities);
    if (!param) return;
    remember(param);
    const day = chosenDate();
    window.location.assign(
      timingHubHref(tool, locale, {
        city: param,
        date: dateSelectable && day !== today ? day : null,
      })
    );
  }

  return (
    <div className="card-sacred mb-6 p-4" data-testid="timing-city-form">
      <form
        ref={formRef}
        translate="no"
        method="get"
        action={action}
        onSubmit={handleSubmit}
        className="flex flex-wrap items-end gap-3"
      >
        <label className="flex min-w-[12rem] flex-1 flex-col text-body-small font-semibold text-ish-dark">
          {copy.city}
          {hydrated ? (
            <>
              {/* Date + Show still submit the current city. */}
              <input type="hidden" name={TIMING_CITY_PARAM} value={city} />
              <TimingCitySelector
                variant="field"
                fieldRef={fieldRef}
                city={{ name: cityName, latitude: 0, longitude: 0 }}
                onCitySelect={() => undefined}
                onPlaceSelect={handlePlace}
                locale={locale}
                fieldClassName={`${CONTROL_CLASS} w-full`}
                // Shubh Muhurat is computed on the India clock; its hub ignores a
                // foreign city, so its search offers only Indian places.
                countryFilter={tool === "shubh-muhurat" ? "IN" : undefined}
              />
            </>
          ) : (
            <select
              name={TIMING_CITY_PARAM}
              defaultValue={city}
              onChange={(event) => event.currentTarget.form?.requestSubmit()}
              className={CONTROL_CLASS}
            >
              {inCatalog ? null : <option value={city}>{cityName}</option>}
              {cities.map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.name}, {option.state}
                </option>
              ))}
            </select>
          )}
        </label>
        {dateSelectable ? (
          <label className="flex flex-col text-body-small font-semibold text-ish-dark">
            {copy.date}
            <input
              type="date"
              name={TIMING_DATE_PARAM}
              defaultValue={date}
              min={min}
              max={max}
              className={CONTROL_CLASS}
            />
          </label>
        ) : null}
        <button
          type="submit"
          className="btn-sacred min-h-11 rounded-lg px-4 py-2 text-body font-semibold"
        >
          {copy.show}
        </button>
      </form>
      {/* The same line before and after hydration, so the card never resizes. */}
      <p className="mt-2 text-center text-caption text-ish-text-faint">{copy.hint}</p>
    </div>
  );
}
